/**
 * The footprints this plan has used, **per equipment type**.
 *
 * The wall-thickness memory with one extra dimension, and for the same reason:
 * a size is a drawing habit, not a property of the plan. Per type because the
 * habit is per type — the radiators on one floor tend to share a size, and that
 * size says nothing about the wash basins.
 *
 * Session-scoped and keyed by the frame asset, exactly like
 * [useWallThickness]: it survives a reload while one floor is being traced,
 * never reaches the server, and does not follow the user to the next floor.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

/** How many distinct footprints each type keeps within reach. */
export const SIZE_MEMORY = 5

export interface EquipmentSize {
    width: number
    depth: number
}

type SizesByType = Record<string, EquipmentSize[]>

const storageKey = (assetUid: string): string => `plan-editor:equipment-size:${assetUid}`

const usable = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0

const same = (a: EquipmentSize, b: EquipmentSize): boolean =>
    a.width === b.width && a.depth === b.depth

/** Most-recent first, distinct, capped — the oldest falls off the end. */
export function rememberSize(recent: EquipmentSize[], size: EquipmentSize): EquipmentSize[] {
    return [size, ...recent.filter((v) => !same(v, size))].slice(0, SIZE_MEMORY)
}

function storedSizes(assetUid: string): SizesByType {
    try {
        const raw = sessionStorage.getItem(storageKey(assetUid))
        const parsed: unknown = raw ? JSON.parse(raw) : null
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
        const out: SizesByType = {}
        for (const [code, sizes] of Object.entries(parsed as Record<string, unknown>)) {
            if (!Array.isArray(sizes)) continue
            const clean = sizes
                .filter((s): s is EquipmentSize =>
                    !!s && typeof s === 'object'
                    && usable((s as EquipmentSize).width) && usable((s as EquipmentSize).depth))
                .slice(0, SIZE_MEMORY)
            if (clean.length) out[code] = clean
        }
        return out
    } catch {
        // Unavailable or unparseable storage: start from nothing.
        return {}
    }
}

export interface EquipmentSizeMemory {
    /** The footprints used for this type, most recent first. */
    recentFor: (typeCode: string | undefined) => EquipmentSize[]
    remember: (typeCode: string | undefined, size: EquipmentSize) => void
}

export function useEquipmentSizes(assetUid: string): EquipmentSizeMemory {
    const [sizes, setSizes] = useState<SizesByType>(() => storedSizes(assetUid))

    // The route reuses this component across frames, so the memory follows the
    // plan rather than the mount — same reasoning as the thickness memory.
    const loadedFor = useRef(assetUid)
    useEffect(() => {
        if (loadedFor.current === assetUid) return
        loadedFor.current = assetUid
        setSizes(storedSizes(assetUid))
    }, [assetUid])

    const remember = useCallback((typeCode: string | undefined, size: EquipmentSize) => {
        if (!typeCode || !usable(size.width) || !usable(size.depth)) return
        setSizes((prev) => {
            const rounded = { width: Math.round(size.width), depth: Math.round(size.depth) }
            const next = { ...prev, [typeCode]: rememberSize(prev[typeCode] ?? [], rounded) }
            try {
                sessionStorage.setItem(storageKey(assetUid), JSON.stringify(next))
            } catch {
                // Storage is a convenience; the size still applies.
            }
            return next
        })
    }, [assetUid])

    const recentFor = useCallback(
        (typeCode: string | undefined): EquipmentSize[] => (typeCode ? sizes[typeCode] ?? [] : []),
        [sizes],
    )

    return { recentFor, remember }
}
